// V3 — distribute a (discipline × channel) total FTE into per-level cells via levelWeights.

import type {
  ClientProfile,
  LevelId,
  LevelMixOverride,
  LevelWeightRow,
  Model,
} from '../types.js';
import { lookupLevelWeights } from './table-lookup.js';
import { LEVEL_BUCKETS, clampShift, tiltWeights } from './level-buckets.js';
import type { LevelBucketId } from './level-buckets.js';

/** Sum & normalize a weights row to guard against rows that don't quite sum to 1. */
export function normalizedWeights(row: LevelWeightRow | undefined): Partial<Record<LevelId, number>> {
  if (!row) return {};
  const entries = Object.entries(row.weights || {}) as [LevelId, number][];
  const sum = entries.reduce((s, [, v]) => s + (Number(v) || 0), 0);
  if (sum <= 1e-9) return {};
  const out: Partial<Record<LevelId, number>> = {};
  for (const [k, v] of entries) {
    out[k] = (Number(v) || 0) / sum;
  }
  return out;
}

/** Distribute total FTE across the model's levels via the given level-weights row. */
export function distributeByLevel(
  model: Model,
  totalFte: number,
  weightsRow: LevelWeightRow | undefined,
): Partial<Record<LevelId, number>> {
  const out: Partial<Record<LevelId, number>> = {};
  if (totalFte <= 0) return out;
  const w = normalizedWeights(weightsRow);
  for (const lvl of model.levels || []) {
    const share = Number(w[lvl.id]) || 0;
    if (share > 0) out[lvl.id] = totalFte * share;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Leveling — scenario-driven seniority tilt
// ---------------------------------------------------------------------------

function sameChannel(a: string | undefined, b: string | undefined): boolean {
  return (a ?? 'all') === (b ?? 'all');
}

/** Resolve the seniority shift for a (discipline, channel): exact channel
 *  match first, then a discipline-wide (`'all'`) entry. 0 when none. */
export function resolveLevelShift(
  profile: ClientProfile,
  disciplineId: string,
  channelId: string,
): number {
  const list: LevelMixOverride[] = profile.levelMix || [];
  const forDiscipline = list.filter((e) => e.disciplineId === disciplineId);
  const exact = forDiscipline.find((e) => sameChannel(e.channelId, channelId));
  const wide = forDiscipline.find((e) => sameChannel(e.channelId, 'all'));
  return clampShift((exact ?? wide)?.shift);
}

/** Base level-weights row with any scenario seniority tilt applied. Returns
 *  the untouched base row when there is no (non-zero) override. */
export function resolveLevelWeights(
  model: Model,
  profile: ClientProfile,
  disciplineId: string,
  channelId: string,
): LevelWeightRow | undefined {
  const base = lookupLevelWeights(model, disciplineId, channelId);
  const shift = resolveLevelShift(profile, disciplineId, channelId);
  if (!base || shift === 0) return base;
  return { ...base, weights: tiltWeights(base.weights, shift, model.seniorityTiltStrength) };
}

/**
 * Preview the resulting normalized Junior/Management/Executive shares for a
 * given seniority `shift`, plus which buckets are non-empty in the base model
 * (so the UI can dim buckets that will always stay 0). Total FTE is unchanged
 * by the tilt; this only reports composition.
 */
export function levelMixPreview(
  model: Model,
  disciplineId: string,
  channelId: string,
  shift: number,
): { buckets: Record<LevelBucketId, number>; active: Record<LevelBucketId, boolean> } {
  const base = lookupLevelWeights(model, disciplineId, channelId);
  const tilted = normalizedWeights(
    base
      ? { ...base, weights: tiltWeights(base.weights, shift, model.seniorityTiltStrength) }
      : undefined,
  );
  const buckets = {} as Record<LevelBucketId, number>;
  const active = {} as Record<LevelBucketId, boolean>;
  for (const b of LEVEL_BUCKETS) {
    let share = 0;
    let baseSum = 0;
    for (const lvl of b.levels) {
      share += Number(tilted[lvl]) || 0;
      baseSum += Number(base?.weights?.[lvl]) || 0;
    }
    buckets[b.id] = share;
    active[b.id] = baseSum > 0;
  }
  return { buckets, active };
}
